import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import waglLogo from '../assets/images/wagl-logo.svg';
import palette from '../assets/sass/palette.scss';

// Keyframe animations
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const LoadingScreen = () => {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${palette.white} 0%, ${palette.palePurple} 25%, ${palette.lightPurple} 50%, ${palette.palePurple} 75%, ${palette.white} 100%)`,
        backgroundSize: '400% 400%',
        animation: `${gradientShift} 3s ease-in-out infinite`,
        zIndex: 9999,
      }}
    >
      {/* Animated background circles */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${palette.lightPurple}20 0%, transparent 70%)`,
          animation: `${pulse} 2s ease-in-out infinite`,
          animationDelay: '0s',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '60%',
          right: '15%',
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${palette.mediumPurple}20 0%, transparent 70%)`,
          animation: `${pulse} 2s ease-in-out infinite`,
          animationDelay: '1s',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          left: '20%',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${palette.greenSharp}20 0%, transparent 70%)`,
          animation: `${pulse} 2s ease-in-out infinite`,
          animationDelay: '0.5s',
        }}
      />

      {/* Main content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          animation: `${fadeIn} 0.8s ease-out`,
        }}
      >
        {/* Logo with pulse animation */}
        <Box
          sx={{
            animation: `${pulse} 2s ease-in-out infinite`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={waglLogo}
            alt="Wagl"
            style={{
              width: '120px',
              height: 'auto',
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))',
            }}
          />
        </Box>

        {/* Loading spinner */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress
            size={40}
            thickness={4}
            sx={{
              color: palette.primary,
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
        </Box>

        {/* Loading text */}
        <Typography
          variant="h6"
          sx={{
            color: palette.mediumGrey,
            fontWeight: 500,
            letterSpacing: '0.5px',
            textAlign: 'center',
            animation: `${fadeIn} 0.8s ease-out 0.2s both`,
          }}
        >
          Loading your experience...
        </Typography>

        {/* Subtitle */}
        <Typography
          variant="body2"
          sx={{
            color: palette.grey,
            textAlign: 'center',
            maxWidth: '300px',
            animation: `${fadeIn} 0.8s ease-out 0.4s both`,
          }}
        >
          Setting up your golf game organizer
        </Typography>
      </Box>
    </Box>
  );
};

export default LoadingScreen;
